/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useQuickList.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing the quick list modal for Speed Dials.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSpeedDials } from '../api/speedDialsApi';
import type { SpeedDial } from '../api/speedDialsApi';

export function useQuickList(mode: 'create' | 'edit', id?: string, duplicateId?: string | null) {
  const navigate = useNavigate();

  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [quickListItems, setQuickListItems] = useState<SpeedDial[]>([]);
  const [isQuickListLoading, setIsQuickListLoading] = useState(false);
  const quickListLoadingRef = useRef(false);

  const currentItemId = useMemo(() => {
    if (mode === 'edit' && id && id !== 'new' && !duplicateId) {
      return id;
    }
    return null;
  }, [mode, id, duplicateId]);

  const loadQuickList = useCallback(async () => {
    if (quickListLoadingRef.current) return;

    quickListLoadingRef.current = true;
    setIsQuickListLoading(true);
    try {
      const params = {
        page: 1,
        limit: 100, // API limit is 100
        sortBy: 'speedCode',
        sortOrder: 'asc' as const
      };

      const response = await getSpeedDials(params);
      const rawItems = Array.isArray(response?.data) ? response.data : [];

      // If we got 100 results, try to load more pages
      let allItems = [...rawItems];
      if (rawItems.length === 100 && response?.pagination) {
        const totalPages = response.pagination.totalPages || Math.ceil((response.pagination.total || 0) / 100);
        for (let page = 2; page <= totalPages && page <= 10; page++) {
          // Limit to 10 pages max (1000 items)
          const nextParams = { ...params, page };
          const nextResponse = await getSpeedDials(nextParams);
          if (Array.isArray(nextResponse?.data)) {
            allItems = [...allItems, ...nextResponse.data];
          }
        }
      }

      setQuickListItems(allItems);
    } catch (error) {
      setQuickListItems([]);
    } finally {
      setIsQuickListLoading(false);
      quickListLoadingRef.current = false;
    }
  }, []);

  // Load quick list when modal opens
  useEffect(() => {
    if (showQuickList && quickListItems.length === 0 && !quickListLoadingRef.current) {
      loadQuickList();
    }
  }, [showQuickList, quickListItems.length, loadQuickList]);

  // Filter items client-side
  const filteredQuickList = useMemo(() => {
    const items = quickListItems.filter(item => item.id != null);
    if (!quickListSearch.trim()) {
      return items;
    }
    const searchLower = quickListSearch.toLowerCase();
    return items.filter(
      item =>
        item.speedCode?.toLowerCase().includes(searchLower) ||
        item.destination?.toLowerCase().includes(searchLower) ||
        item.description?.toLowerCase().includes(searchLower)
    );
  }, [quickListItems, quickListSearch]);

  const handleItemSelect = useCallback(
    (item: SpeedDial) => {
      setShowQuickList(false);
      navigate(`/pbx/applications/speed-dialing/${item.uuid || item.id}`);
    },
    [navigate]
  );

  return {
    showQuickList,
    setShowQuickList,
    quickListSearch,
    setQuickListSearch,
    filteredQuickList,
    isQuickListLoading,
    handleItemSelect,
    currentItemId
  };
}

